import type {
	AddonType,
	FeatureType,
	ResourceType,
	SubscriptionCycle,
	SubscriptionPlan,
} from "./types";

export const PLAN_NAMES: Record<SubscriptionPlan, string> = {
	BUSINESS: "Business",
	ENTERPRISE: "Enterprise",
	AGENCY: "Agency",
};

export const BILLING_CYCLE_LABELS: Record<SubscriptionCycle, string> = {
	MONTHLY: "monthly",
	ANNUAL: "annually",
	TWO_YEARS: "every 2 years",
	THREE_YEARS: "every 3 years",
	FOUR_YEARS: "every 4 years",
	FIVE_YEARS: "every 5 years",
};

export const ADDON_PRICES: Partial<Record<AddonType, number>> = {
	"admin-seat": 49,
	"editor-seat": 19,
	"addon-maps": 25,
	"addon-storage": 50,
};

export const PLAN_LIMITS: Record<
	SubscriptionPlan,
	{
		maps: number;
		storage: number;
		privateViewers: number | null;
	}
> = {
	BUSINESS: {
		maps: 20,
		storage: 4,
		privateViewers: 0,
	},
	ENTERPRISE: {
		maps: 40,
		storage: 8,
		privateViewers: 100,
	},
	AGENCY: {
		maps: 100,
		storage: 16,
		privateViewers: null,
	},
};

export const RESOURCE_NAMES: Record<ResourceType, string> = {
	maps: "maps",
	storage: "storage",
	"private-viewers": "private viewers",
};

export const FEATURE_NAMES: Record<FeatureType, string> = {
	"admin-seats": "Admin Seats",
	"editor-seats": "Editor Seats",
	"additional-maps": "Additional Maps",
	"additional-storage": "Additional Storage",
	"private-viewers": "Private Viewers",
	"group-access-control": "Group Access Control",
	"audit-log": "Audit Log",
	"white-label": "White Label",
	"dropbox-integration": "Dropbox Integration",
	"google-analytics": "Google Analytics",
};

export const FEATURE_DESCRIPTIONS: Record<FeatureType, string> = {
	"admin-seats":
		"Collaborate faster by adding team members who can create and manage maps.",
	"editor-seats":
		"Keep data current with team members who can edit directly in the field.",
	"additional-maps":
		"Tell more stories—expand your map library as your projects grow.",
	"additional-storage": "Room to grow—store more datasets, imagery, and files.",
	"private-viewers":
		"Control who sees your maps with invite-only access for specific viewers.",
	"group-access-control":
		"Simplify permissions by managing map access for entire teams at once.",
	"audit-log":
		"Stay informed with a complete history of changes across your account.",
	"white-label": "Share maps without any MangoMap branding.",
	"dropbox-integration": "Automate data updates via Dropbox.",
	"google-analytics":
		"Understand your audience with visitor insights on every map.",
};

export const LIMIT_REACHED_TITLES: Record<ResourceType, string> = {
	maps: "Ready for more maps?",
	storage: "Need more space?",
	"private-viewers": "Growing your audience?",
};

export const LIMIT_REACHED_MESSAGES: Record<
	ResourceType,
	(current: number, limit: number, unit?: string) => string
> = {
	maps: (_current) => `Upgrade to keep building and unlock more features.`,
	storage: (_current) =>
		`Upgrade for more space to store your datasets and imagery.`,
	"private-viewers": (_current) => `Go unlimited with Agency.`,
};

export const getFeatureUnavailableTitle = (featureName: string): string =>
	`Unlock ${featureName}`;

export const PAID_UPGRADE_CTA = "Upgrade Now";
export const TRIAL_UPGRADE_CTA = "Switch Plan";
export const START_SUBSCRIPTION_CTA = "Start Subscription";
export const DISMISS_CTA = "Not now";
export const CONFIRM_ADDON_CTA = "Add Seat";
export const CANCEL_CTA = "Maybe later";

// Addon confirmation constants (for Enterprise/Agency users adding seats)
export const ADDON_NAMES: Record<AddonType, string> = {
	"admin-seat": "Admin Seat",
	"editor-seat": "Editor Seat",
	"addon-maps": "Additional Maps",
	"addon-storage": "Additional Storage",
};

export const ADDON_DESCRIPTIONS: Record<AddonType, string> = {
	"admin-seat":
		"Admins can create maps, manage datasets, and update account settings.",
	"editor-seat":
		"Many hands make light work. Keep data up to date with editors.",
	"addon-maps": "Expand your map library with additional map slots.",
	"addon-storage":
		"Increase your storage capacity for more datasets and imagery.",
};

export const ADDON_BLOCK_CONFIG = {
	"addon-maps": { size: 5, unit: "maps", maxBlocks: 4 },
	"addon-storage": { size: 50, unit: "GB", maxBlocks: 4 },
} as const;

export const ADDON_CONFIRMATION_TITLE = "Add a new team member";

export const ADDON_BILLING_NOTE =
	"Seats are billed monthly. You'll be charged a prorated amount for the rest of this billing cycle.";
